import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import classNames from 'classnames';
import type { AgGridReact } from 'ag-grid-react';
import {
  ColumnRowGroupChangedEvent,
  GridApi,
  IRowNode,
  IsGroupOpenByDefaultParams,
  RowDragEndEvent,
  RowGroupOpenedEvent,
  SortChangedEvent,
} from 'ag-grid-community';
import { isPresentationWorkbookMode } from '@/utilities/utilities';
import { getStripedColor } from '@/utilities/tableBuilderHelper.utilities';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { AgGridAsync } from '@/core/tableUtilities/AgGridAsync';
import useAgGridProps from '@/tableBuilder/hooks/useAgGridProps';
import { AgGridRowIds, TableBuilderSimpleAgGridProps } from './tableBuilder.types';
import TableBuilderAgGridChartView from './TableBuilderAgGridChartView.molecule';
import {
  ASSET_PATH_ID_COLUMN_PREFIX,
  COLUMN_HEADER_ID,
  DEFAULT_TABLE_ROW_HEIGHT,
  ROW_ID,
  SEEQ_AG_GRID_ID,
  SEEQ_ROW_INDEX,
  SIMPLE_TABLE_STRIPED_FIELD,
  SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN,
  TableBuilderColumnType,
  TableBuilderHeaderType,
  TableBuilderMode,
} from './tableBuilder.constants';
import {
  createSimpleColDefs,
  getFullGroupedNodePath,
  getMoreRowsProps,
  getSimpleTableHeaderComponentParams,
  onRowDragEnd,
} from './tableBuilderAgGrid.utilities';
import { COLUMNS_AND_STATS, ITEM_TYPES, TREND_COLORS } from '@/trendData/trendData.constants';
import { getStatisticalGraphType } from '@/tableBuilder/tableBuilderAgChart.utilities';
import { setAreAllRowsExpanded } from '@/tableBuilder/tableBuilderAgGrid.actions';
import { TableBuilderTreemap } from '@/tableBuilder/TableBuilderTreemap.organism';
import { setTreemapViewSettings } from './tableBuilder.actions';

export const TableBuilderSimpleAgGrid: React.FunctionComponent<TableBuilderSimpleAgGridProps> = (
  props: TableBuilderSimpleAgGridProps,
) => {
  const {
    autoGroupColumn,
    simpleColumns,
    simpleTableData,
    isTransposed,
    isStriped,
    isPresentationMode,
    darkMode,
    displayRange,
    setAgGridElement,
    updateContentMeasurements,
    moveColumn,
    rowGroupPaths,
    handleSwapAssets,
    handleRowGroupOpened,
    handleRowGroupChanged,
    sortByColumn,
    showChartView,
    showTreemapView,
    useSignalColorsInChart,
    chartColors,
    chartViewSettings,
    areAllRowsExpanded,
    timezone,
    distinctStringValueMap,
    chartMenuSettings,
    tableItems,
    treemapViewSettings,
  } = props;

  const chartWrapperRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const rowNodeOutsideGridRef = useRef<IRowNode>(undefined);
  const [agGridApi, setAgGridApi] = useState<GridApi>();
  const isInteractiveContent = !_.isNil(updateContentMeasurements);
  const { t } = useTranslation();

  const conditions = _.filter(tableItems, (item) => item.itemType === ITEM_TYPES.CONDITION);
  const getCustomColors = () => simpleTableData.map((row) => chartColors?.[row.itemId]);
  const chartViewColors = useSignalColorsInChart && chartColors ? getCustomColors() : TREND_COLORS;

  const statisticalGraphType = getStatisticalGraphType(chartViewSettings?.chartType);

  const hiddenIndexes = useMemo(() => {
    if (!showChartView) {
      return simpleColumns
        .map((col, colIndex) => {
          return col.key.startsWith(ASSET_PATH_ID_COLUMN_PREFIX) ? colIndex : null;
        })
        .filter((index) => index !== null);
    }

    return simpleColumns
      .map((col, colIndex) =>
        col.key.startsWith('statistic') || col.key === COLUMNS_AND_STATS.metricValue.key ? null : colIndex,
      )
      .filter((index) => index !== null);
  }, [showChartView, simpleColumns]);

  const setAgGridElementWrapper = (el: AgGridReact) => {
    if (setAgGridElement) {
      setAgGridElement(el);
    }
    if (wrapperRef.current) {
      (wrapperRef.current as any).__AG_GRID__ = el;
    }
  };

  const columnDefs = useMemo(
    () => createSimpleColDefs(props, showChartView),
    [
      showChartView,
      isTransposed,
      simpleColumns,
      isStriped,
      simpleTableData,
      statisticalGraphType,
      distinctStringValueMap,
      darkMode,
    ],
  );

  const normalRows = useMemo((): Record<string, any>[] => {
    const rowData: Record<string, any>[] = [];
    simpleTableData.forEach((row, rowIndex) => {
      const agGridRow: Record<string, any> = {};
      agGridRow[SIMPLE_TABLE_STRIPED_FIELD] = getStripedColor(isStriped, rowIndex, darkMode);

      const hasSourceItem = row.itemId !== row.formulaItemId;
      agGridRow[ROW_ID] = row.itemId + (hasSourceItem ? row.formulaItemId : rowIndex.toString());
      agGridRow[SEEQ_ROW_INDEX] = rowIndex;

      let columnIndex = 0;
      columnDefs.forEach((def) => {
        const column = simpleColumns[columnIndex];
        const cell = row.cells[columnIndex];

        const fieldName = def.field!;
        agGridRow[fieldName] = column?.type === TableBuilderColumnType.Text ? column?.cells?.[row.itemId] : cell;

        columnIndex++;
      });

      rowData.push(agGridRow);
    });

    return rowData;
  }, [darkMode, isStriped, simpleColumns, columnDefs, simpleTableData]);

  const transposedRows = useMemo((): Record<string, any>[] => {
    return simpleColumns
      .filter((_, columnIndex) => !hiddenIndexes.includes(columnIndex))
      .map((column, columnIndex) => {
        const agGridRow: Record<string, any> = {};
        agGridRow[ROW_ID] = column.key;

        _.forEach(simpleTableData, (row, rowIndex) => {
          const filteredCells = row.cells.filter((_, index) => !hiddenIndexes.includes(index));
          const cell = filteredCells[columnIndex];

          if (simpleColumns[columnIndex].type === TableBuilderColumnType.Text) {
            agGridRow[rowIndex.toString()] = column?.cells?.[row.itemId];
          } else {
            agGridRow[rowIndex.toString()] = cell;
          }
        });

        agGridRow[SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN] = {
          headerComponentParams: getSimpleTableHeaderComponentParams(props, column, columnIndex, false),
        };

        agGridRow[COLUMN_HEADER_ID] = column.key;

        return agGridRow;
      });
  }, [darkMode, isStriped, simpleColumns, columnDefs, simpleTableData]);

  const data = isTransposed ? transposedRows : normalRows;

  const rowIds: AgGridRowIds = data?.reduce((acc, row, index) => {
    acc[row[ROW_ID]] = { index };

    return acc;
  }, {});

  const signals = useMemo(
    () =>
      _.chain(tableItems)
        .filter(
          (signal) =>
            _.some(signal.assets, (asset) => _.includes(conditions?.[0]?.assets?.[0]?.pathComponentIds, asset.id)) &&
            (signal.itemType === ITEM_TYPES.SERIES || signal.itemType === ITEM_TYPES.SCALAR),
        )
        .map((signal) => ({ ...signal, value: signal.id, label: signal.name }))
        .value(),
    [tableItems, conditions],
  );
  /** --------- Charts -----------  */
  const shouldAutoSize = useMemo(
    () => agGridApi && !showChartView && simpleColumns.length > 0,
    [showChartView, simpleColumns, agGridApi],
  );

  const { autoSizeHelper, sharedAgGridProps, showTable } = useAgGridProps({
    agGridApi,
    chartWrapperRef,
    columnDefs,
    data,
    setAgGridApi,
    showChartView,
    shouldAutoSize,
    props,
    rowNodeOutsideGridRef,
    simpleTableData,
    t,
    tableBuilderMode: TableBuilderMode.Simple,
    wrapperRef,
    rowIds,
    chartViewColors,
    chartMenuSettings,
  });

  /* AgGrid Handlers */
  const handleRowGroupOpenedChange = (event: RowGroupOpenedEvent) => {
    // If the event is triggered by a user action, we reset the areAllRowsExpanded state
    if (!_.isNull(event.event)) {
      setAreAllRowsExpanded(undefined);
    }
    handleRowGroupOpened(event);
    autoSizeHelper();
  };

  const handleRowGroupChangedSimpleTable = (event: ColumnRowGroupChangedEvent) => {
    const nodes = event.api.getRenderedNodes();
    nodes.forEach((node) => {
      if (areAllRowsExpanded) {
        node.setExpanded(true);
      }
    });

    handleRowGroupChanged(event);
    autoSizeHelper();
  };

  const isGroupOpenByDefaultSimpleTable = (params: IsGroupOpenByDefaultParams) => {
    if (!params?.rowNode.key) {
      return false;
    }
    return areAllRowsExpanded || rowGroupPaths.includes(getFullGroupedNodePath(params.rowNode));
  };

  const handleRowDragStop = () => {
    if (rowNodeOutsideGridRef.current) {
      onRowDragEnd(rowNodeOutsideGridRef.current, simpleColumns, moveColumn, rowNodeOutsideGridRef);
    }
  };

  const handleRowDragEnd = (event: RowDragEndEvent) => {
    onRowDragEnd(event.node, simpleColumns, moveColumn, rowNodeOutsideGridRef);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        id={SEEQ_AG_GRID_ID}
        data-testid="simpleTable"
        className={classNames('flexFillOverflow overflowAuto', {
          hidden: showTreemapView || showChartView || (!showTable && !isInteractiveContent && !headlessRenderMode()),
        })}>
        <AgGridAsync
          className={classNames({ 'transposed-simple-table': isTransposed })}
          columnDefs={columnDefs}
          headerHeight={props.headers.type === TableBuilderHeaderType.None ? 0 : DEFAULT_TABLE_ROW_HEIGHT}
          isGroupOpenByDefault={isGroupOpenByDefaultSimpleTable}
          onColumnRowGroupChanged={handleRowGroupChangedSimpleTable}
          onDragStopped={handleRowDragStop}
          onRowDragEnd={handleRowDragEnd}
          onRowGroupOpened={handleRowGroupOpenedChange}
          ref={setAgGridElementWrapper}
          rowDragText={() => ''}
          rowGroupPanelShow={isTransposed || !autoGroupColumn ? 'never' : 'always'}
          suppressMaxRenderedRowRestriction={true}
          suppressRowVirtualisation={isPresentationWorkbookMode() && headlessRenderMode()}
          onSortChanged={(params) => {
            if (!isTransposed && params.source !== 'gridOptionsChanged' && params.columns) {
              const [sortedColumns, nonSortedColumns] = _.partition(params.columns, (column) => column.getSort());
              sortedColumns.forEach((column) => {
                sortByColumn(column.getColId(), column.getSort()!);
              });
              nonSortedColumns.forEach((column) => {
                sortByColumn(column.getColId(), undefined);
              });
            }
          }}
          alwaysMultiSort={true}
          {...sharedAgGridProps}
          {...getMoreRowsProps(data.length)}
        />
      </div>
      {showTreemapView && (
        <TableBuilderTreemap
          agGridApi={agGridApi}
          conditions={conditions}
          displayRange={displayRange}
          handleSwapAssets={handleSwapAssets}
          isPresentationMode={isPresentationMode}
          timezone={timezone}
          treemapViewSettings={treemapViewSettings}
          setTreemapViewSettings={setTreemapViewSettings}
          signals={signals}
          simpleColumns={simpleColumns}
        />
      )}
      <TableBuilderAgGridChartView chartWrapperRef={chartWrapperRef} showChartView={showChartView} />
    </>
  );
};
